import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { switchMap, filter, take, map, catchError } from 'rxjs/operators';

import { SentryLogger, AppState, ConfigService } from '@fcom/core';
import { loginToken } from '@fcom/core/selectors';
import { before, TimeUnit, add } from '@fcom/core/utils';

import { VouchersResponse, Voucher, VoucherProductName } from '../interfaces';

export enum UpgradeBenefitsState {
  LOADING = 'loading',
  COMPLETED = 'completed',
  ERROR = 'error',
}

@Injectable()
export class VoucherService {
  private accessToken$: Observable<string>;
  private vouchers: Voucher[];
  status$: BehaviorSubject<UpgradeBenefitsState> = new BehaviorSubject<UpgradeBenefitsState>(
    UpgradeBenefitsState.LOADING
  );

  constructor(
    private http: HttpClient,
    private sentryLogger: SentryLogger,
    private store$: Store<AppState>,
    private configService: ConfigService
  ) {
    this.accessToken$ = this.store$.pipe(loginToken(), filter(Boolean), take(1)) as Observable<string>;
  }

  getVouchers(): Observable<Voucher[]> {
    if (this.vouchers) {
      return of(this.vouchers);
    }

    const apiUrl = this.configService.cfg.loyaltyServices.legacy.apiProfileUrl;
    const xApiKey = this.configService.cfg.loyaltyServices.legacy.apiProfileApiKey;

    this.status$.next(UpgradeBenefitsState.LOADING);
    return this.accessToken$.pipe(
      switchMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'x-api-key': xApiKey,
            oauth_token: token,
          }),
        };

        return this.http.post<VouchersResponse>(apiUrl, { vouchersRequest: {} }, httpOptions).pipe(
          map((response) => {
            if (response.vouchers.partStatus.code === 'SUCCESS') {
              this.status$.next(UpgradeBenefitsState.COMPLETED);
              this.vouchers = response.vouchers.vouchers;
              return response.vouchers.vouchers;
            }
            throw new Error('Error when requesting vouchers');
          }),
          // eslint-disable-next-line rxjs/no-implicit-any-catch
          catchError((err) => {
            this.status$.next(UpgradeBenefitsState.ERROR);
            this.sentryLogger.warn(err.message, { error: err });
            return throwError(() => err);
          })
        );
      })
    );
  }

  expiringTravelClassUpgradeVouchers(vouchers: Voucher[]): Voucher[] {
    return vouchers.filter(isUpgradeVoucher).filter(isExpiringVoucher).sort(sortByExpirationDate);
  }
}

const isUpgradeVoucher = (voucher: Voucher) =>
  voucher.productName === VoucherProductName.EUROPE_UPGRADE ||
  voucher.productName === VoucherProductName.LONG_HAUL_UPGRADE;

const VOUCHER_EXPIRY_MONTHS = 1;

const isExpiringVoucher = (voucher: Voucher) =>
  before(new Date(voucher.expirationDate), add(new Date(), VOUCHER_EXPIRY_MONTHS, TimeUnit.month), TimeUnit.day);

const sortByExpirationDate = (voucherA: Voucher, voucherB: Voucher) =>
  voucherA.expirationDate > voucherB.expirationDate ? -1 : voucherB.expirationDate > voucherA.expirationDate ? 1 : 0;
